import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Label opcional à direita */
  label?: string;
  /** Texto adicional pequeno abaixo do label */
  hint?: string;
  size?: 'sm' | 'md';
}

/**
 * Switch (toggle on/off) — wrap acessível de input checkbox estilizado.
 *
 * Uso:
 *   <Switch label="Ativo" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
 *
 * Acessibilidade: usa <input type="checkbox"> nativo + label clicável.
 * Screen readers funcionam automaticamente.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ label, hint, size = 'md', className, disabled, checked, ...props }, ref) => {
    const trackSize = size === 'sm' ? 'w-8 h-5' : 'w-10 h-6';
    const thumbSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
    const thumbOffset = size === 'sm' ? 'translate-x-3' : 'translate-x-4';

    return (
      <label
        className={cn(
          'inline-flex items-center gap-3 cursor-pointer select-none',
          disabled && 'opacity-60 cursor-not-allowed',
          className
        )}
      >
        <span className="relative flex-shrink-0">
          <input
            ref={ref}
            type="checkbox"
            className="sr-only peer"
            disabled={disabled}
            checked={checked}
            {...props}
          />
          <span
            className={cn(
              'block rounded-full transition-colors',
              'bg-line dark:bg-line-dark',
              'peer-checked:bg-red',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-red-soft dark:peer-focus-visible:ring-red-soft-dark',
              trackSize
            )}
          />
          <span
            className={cn(
              'absolute top-0.5 left-0.5 rounded-full bg-white shadow transition-transform',
              'peer-checked:' + thumbOffset,
              checked && thumbOffset,
              thumbSize
            )}
          />
        </span>
        {(label || hint) && (
          <span className="flex flex-col">
            {label && <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">{label}</span>}
            {hint && <span className="text-xs text-ink-3 dark:text-ink-dark-3">{hint}</span>}
          </span>
        )}
      </label>
    );
  }
);
Switch.displayName = 'Switch';
